// Module 10 — Portfolio.
// Route: /reinsurance/portfolio
//
// The only one of the ten screens that is not list-plus-drawer: it is an
// analytics page, so it is built here rather than on ModulePage.
//
// Everything comes from ONE endpoint. Every figure on the page is a
// different cut of the same active book over the same window, and fetching
// them separately would let the donut and the trend chart disagree about
// what was in scope — the classic dashboard bug where the parts do not add
// up to the whole because they were fetched a second apart.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/AppLayout';
import {
  BASE_URL, C, S, Icons, Kpi, KpiRow, Pill, PageHeader,
  PrimaryButton, GhostButton, ViewAllLink, Spinner,
  money0, moneyShort, date, humanise,
} from './kit';
import { Donut, StackedBars, BarList, InsightCard } from './charts';

// A panel on the analytics grid. Same edges and padding as the drawer's
// Panel, but it owns a heading row with an optional control on the right.
// `flushBody` for a card whose CONTENT runs to the edges — a table that
// supplies its own cell padding. The card drops its horizontal padding for the
// body's sake and the header takes that padding itself, so the heading still
// lines up with the first column instead of hanging 18px to its left.
//
// "Key programs" passed `style={{ padding: '16px 0 0' }}` to get the flush
// table and took the header's padding away with it: the title sat at x=0, the
// column headings at x=18, and "View all programs" was jammed against the
// right edge. QA logged it as an alignment issue on exactly that panel.
const CARD_PAD_X = 18;

function Card({ title, control, children, style, flushBody = false }) {
  return (
    <section style={{
      ...S.card, minWidth: 0,
      padding: flushBody ? '16px 0 0' : `16px ${CARD_PAD_X}px 18px`,
      ...style,
    }}>
      {/* baseline, not center: a 14.5px bold heading beside a 12px link sat
          visibly off by a couple of pixels when centred on the box. */}
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 12, marginBottom: 14,
        ...(flushBody ? { padding: `0 ${CARD_PAD_X}px` } : null),
      }}>
        <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: C.ink }}>{title}</h2>
        {control}
      </div>
      {children}
    </section>
  );
}

// Risk flag on a program. Semantic colour, separate from the status pill
// beside it: one says how exposed we are, the other says whether the treaty
// is live, and they are not the same question.
function RiskFlag({ value }) {
  if (!value) return <span style={{ color: C.faint }}>—</span>;
  const tint = {
    high:   { fg: C.red,   bg: C.redBg },
    medium: { fg: C.amber, bg: C.amberBg },
    low:    { fg: C.green, bg: C.greenBg },
  }[value] || { fg: C.muted, bg: C.neutralBg };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 11px', borderRadius: 999,
      fontSize: 11.5, fontWeight: 700, background: tint.bg, color: tint.fg,
    }}>
      {humanise(value)}
    </span>
  );
}

// Twelve months by default. A treaty is written for a year and most of a
// book incepts on 1 January, so a six-month window ending mid-year shows one
// column and reads as a broken chart rather than a quiet one.
const WINDOWS = [
  { value: '12m', label: 'Last 12 months', months: 12 },
  { value: '6m',  label: 'Last 6 months', months: 6 },
  { value: 'ytd', label: 'Year to date', months: null },
];

export default function PortfolioScreen() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [win, setWin] = useState('12m');
  const history = useHistory();

  // A CSV of what the screen is showing: territory exposure and the live
  // programmes, in the selected window. Quoted per RFC 4180 — a cedant name
  // with a comma in it would otherwise shift every column after it.
  const exportCsv = useCallback(() => {
    if (!data) return;
    const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [['Section', 'Name', 'Value', 'Currency'].map(q).join(',')];
    for (const tr of (data.territories || [])) {
      lines.push(['Territory', tr.name ?? tr.territory, tr.percent ?? tr.share, '%'].map(q).join(','));
    }
    for (const pr of (data.programs || [])) {
      lines.push(['Programme', pr.name || pr.treatyNumber, pr.premium ?? pr.written, pr.currency ?? ''].map(q).join(','));
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reinsurance-portfolio-${win}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [data, win]);

  const range = useMemo(() => {
    const to = new Date();
    const entry = WINDOWS.find(w => w.value === win) || WINDOWS[0];
    const from = entry.months === null
      ? new Date(Date.UTC(to.getUTCFullYear(), 0, 1))
      : new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth() - (entry.months - 1), 1));
    return { from: from.toISOString(), to: to.toISOString(), label: entry.label };
  }, [win]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await axios.get(`${BASE_URL}/reinsurance/portfolio`, {
        params: { from: range.from, to: range.to },
        withCredentials: true,
      });
      setData(res.data?.data || null);
    } catch (e) {
      // A 403 here is the feature gate, not a bug. Saying which sends the
      // reader to the right place instead of "something went wrong".
      const status = e?.response?.status;
      setErr(status === 403
        ? (e?.response?.data?.message || 'Portfolio is not enabled for your organisation.')
        : (e?.response?.data?.message || e.message || 'Could not load the portfolio'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const h = data?.headline;
  const ccy = data?.window?.currency;
  const insights = data?.insights;

  const select = {
    padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.border}`,
    background: '#fff', color: C.body, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
  };

  return (
    <AppLayout>
      <div style={S.page}>
        <div style={{ ...S.body, display: 'block', overflowY: 'auto' }}>
          <div style={{ padding: '24px 28px 40px' }}>
            <PageHeader
              title={t('screens.rePortfolio.title', 'Portfolio')}
              subtitle={t('screens.rePortfolio.subtitle', 'Exposure, premium and performance across placements')}
              actions={(
                <>
                  {/* "Create watchlist" had no handler and no feature behind
                      it, so it is not offered. Export writes what is on the
                      screen — the same window and the same figures — rather
                      than re-querying and risking a file that disagrees with
                      the page it came from. */}
                  <GhostButton icon={Icons.chart()} onClick={exportCsv} disabled={!data}>
                    {t('screens.rePortfolio.export', 'Export analytics')}
                  </GhostButton>
                </>
              )}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
              <select
                value={win}
                onChange={(e) => setWin(e.target.value)}
                style={select}
                aria-label={t('screens.rePortfolio.window', 'Reporting window')}
              >
                {WINDOWS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
            </div>

            {err && (
              <div style={{
                border: `1px solid ${C.border}`, background: '#fff7f7', color: C.red,
                borderRadius: 10, padding: '11px 14px', fontSize: 13, marginBottom: 14,
              }}>{err}</div>
            )}

            {/* What the page is NOT showing, said out loud. A portfolio total
                that silently omits a third of the book is worse than none. */}
            {data?.excluded?.treaties > 0 && (
              <div style={{
                border: `1px solid ${C.border}`, background: C.amberBg, color: C.amber,
                borderRadius: 10, padding: '11px 14px', fontSize: 12.5, marginBottom: 14, lineHeight: 1.5,
              }}>
                {t('screens.rePortfolio.excluded',
                  'Showing the {{ccy}} book only. {{n}} treaty/treaties in {{other}} are excluded rather than converted — a single FX rate cannot fairly restate programmes written on different dates.',
                  { ccy: ccy || '—', n: data.excluded.treaties, other: (data.excluded.currencies || []).join(', ') || 'other currencies' })}
              </div>
            )}

            <KpiRow>
              <Kpi icon={Icons.db()} tone='blue'
                value={h ? moneyShort(h.grossPremiumCeded?.amount, ccy) : '—'}
                trend={h?.grossPremiumCeded?.trend}
                comparison={h?.grossPremiumCeded?.comparison}
                label={t('screens.rePortfolio.kpiCeded', 'Gross premium ceded')} />
              <Kpi icon={Icons.doc()} tone='amber'
                value={h ? moneyShort(h.recoverablesOutstanding?.amount, ccy) : '—'}
                trend={h?.recoverablesOutstanding?.trend}
                label={t('screens.rePortfolio.kpiRecoverables', 'Recoverables outstanding')} />
              <Kpi icon={Icons.shield()} tone='green'
                value={h?.activeTreaties?.value ?? '—'}
                trend={h?.activeTreaties?.trend}
                label={t('screens.rePortfolio.kpiTreaties', 'Active treaties')} />
              <Kpi icon={Icons.chart()} tone='red'
                // Null, not 0%. A book with no ceded premium has no loss
                // ratio, and 0% would read as an excellent one.
                value={h?.lossRatio?.value === null || h?.lossRatio?.value === undefined
                  ? t('screens.rePortfolio.noRatio', 'No premium')
                  : `${Math.round(h.lossRatio.value)}%`}
                trend={h?.lossRatio?.trend}
                comparison={h?.lossRatio?.comparison}
                label={t('screens.rePortfolio.kpiLoss', 'Portfolio loss ratio')} />
            </KpiRow>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 16, marginBottom: 16,
            }}>
              <Card title={t('screens.rePortfolio.trend', 'Ceded premium trend')} control={<span style={{ fontSize: 12, color: C.muted }}>{range.label}</span>}>
                <StackedBars months={data?.trend?.months || []} currency={ccy} />
              </Card>

              <Card title={t('screens.rePortfolio.mix', 'Portfolio mix by line of business')}>
                <Donut
                  segments={data?.mix?.segments || []}
                  total={data?.mix?.total}
                  currency={ccy}
                  caption={t('screens.rePortfolio.cededPremium', 'Ceded premium')}
                />
              </Card>

              <Card title={t('screens.rePortfolio.geography', 'Geographic exposure')} control={<span style={{ fontSize: 12, color: C.muted }}>{t('screens.rePortfolio.byCeded', 'By ceded premium')}</span>}>
                <BarList segments={data?.geography?.segments || []} />
              </Card>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2.2fr) minmax(280px, 1fr)', gap: 16, alignItems: 'start' }}>
              <Card
                title={t('screens.rePortfolio.programs', 'Key programs')}
                control={<ViewAllLink onClick={() => history.push('/reinsurance/treaties')}>{t('screens.rePortfolio.viewPrograms', 'View all programs')}</ViewAllLink>}
                flushBody
              >
                <div style={{ overflowX: 'auto' }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={{ ...S.th, paddingLeft: 18 }}>{t('screens.rePortfolio.hProgram', 'Program / treaty')}</th>
                        <th style={S.th}>{t('screens.rePortfolio.hCedant', 'Cedant')}</th>
                        <th style={S.th}>{t('screens.rePortfolio.hClass', 'Class of business')}</th>
                        <th style={{ ...S.th, ...S.num }}>{t('screens.rePortfolio.hCeded', 'Ceded premium')}</th>
                        <th style={{ ...S.th, ...S.num }}>{t('screens.rePortfolio.hRecoverables', 'Recoverables outstanding')}</th>
                        <th style={S.th}>{t('screens.rePortfolio.hRenewal', 'Renewal date')}</th>
                        <th style={S.th}>{t('screens.rePortfolio.hRisk', 'Risk flag')}</th>
                        <th style={{ ...S.th, paddingRight: 18 }}>{t('screens.rePortfolio.hStatus', 'Status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr><td colSpan={8} style={{ ...S.td, paddingLeft: 18 }}><Spinner /></td></tr>
                      )}
                      {!loading && !(data?.programs || []).length && (
                        <tr><td colSpan={8} style={{ ...S.td, paddingLeft: 18, color: C.muted }}>
                          {t('screens.rePortfolio.noPrograms', 'No live programs in this window.')}
                        </td></tr>
                      )}
                      {(data?.programs || []).map(p => (
                        <tr key={p._id}>
                          <td style={{ ...S.td, paddingLeft: 18 }}>
                            <div style={{ fontWeight: 600, color: C.ink }}>{p.treatyNumber}</div>
                            <div style={{ fontSize: 12, color: C.muted }}>{p.name}</div>
                          </td>
                          <td style={S.td}>{p.cedantName || '—'}</td>
                          <td style={S.td}>{p.classOfBusiness || '—'}</td>
                          <td style={{ ...S.td, ...S.num }}>{money0(p.cededPremium, p.currency)}</td>
                          <td style={{ ...S.td, ...S.num }}>{money0(p.recoverablesOutstanding, p.currency)}</td>
                          <td style={S.td}>{date(p.renewalDate)}</td>
                          <td style={S.td}><RiskFlag value={p.riskFlag} /></td>
                          <td style={{ ...S.td, paddingRight: 18 }}><Pill status={p.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              <div style={{ display: 'grid', gap: 12 }}>
                <h2 style={{ margin: '2px 0 0', fontSize: 14.5, fontWeight: 700, color: C.ink }}>
                  {t('screens.rePortfolio.insights', 'Portfolio insights')}
                </h2>

                <InsightCard
                  tone='red'
                  icon={Icons.alert()}
                  title={t('screens.rePortfolio.concentration', 'Concentration risk')}
                  value={insights?.concentration?.percent === null || insights?.concentration?.percent === undefined
                    ? null
                    : `${Math.round(insights.concentration.percent)}%`}
                  detail={insights?.concentration
                    ? t('screens.rePortfolio.concentrationDetail',
                      'of ceded premium is {{lob}} business{{where}}. Consider diversification.',
                      {
                        lob: insights.concentration.label,
                        where: insights.concentration.territory ? ` in ${insights.concentration.territory}` : '',
                      })
                    : t('screens.rePortfolio.noConcentration', 'No ceded premium in this window.')}
                />

                <InsightCard
                  tone='blue'
                  icon={Icons.chart()}
                  title={t('screens.rePortfolio.capacity', 'Capacity utilisation')}
                  value={insights?.capacityUtilisation === null || insights?.capacityUtilisation === undefined
                    ? null
                    : `${Math.round(insights.capacityUtilisation)}%`}
                  detail={insights?.capacityUtilisation === null || insights?.capacityUtilisation === undefined
                    // A ratio needs a denominator. Saying which one is
                    // missing is more useful than showing a made-up figure.
                    ? t('screens.rePortfolio.noCapacity', 'No counterparty has a recorded capacity to measure against.')
                    : t('screens.rePortfolio.capacityDetail', 'of total available capacity utilised across active treaties.')}
                />

                <InsightCard
                  tone='amber'
                  icon={Icons.users()}
                  title={t('screens.rePortfolio.counterparty', 'Counterparty exposure')}
                  value={insights?.counterpartyExposure?.amount
                    ? moneyShort(insights.counterpartyExposure.amount, ccy)
                    : null}
                  detail={insights?.counterpartyExposure?.count
                    ? t('screens.rePortfolio.counterpartyDetail',
                      'outstanding recoverables with the top {{n}} counterparties ({{pct}} of total).',
                      {
                        n: insights.counterpartyExposure.count,
                        pct: insights.counterpartyExposure.percent === null || insights.counterpartyExposure.percent === undefined
                          ? '—'
                          : `${Math.round(insights.counterpartyExposure.percent)}%`,
                      })
                    : t('screens.rePortfolio.noRecoverables', 'No outstanding recoverables.')}
                />

                <InsightCard
                  tone='green'
                  icon={Icons.cal()}
                  title={t('screens.rePortfolio.renewals', 'Upcoming renewals')}
                  value={insights?.upcomingRenewals ?? 0}
                  detail={t('screens.rePortfolio.renewalsDetail', 'treaties renewing in the next 90 days.')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
